import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { supabase } from './lib/supabase';
import { useAuthStore } from './store/authStore';
import { LanguageProvider } from './lib/i18n';
import Navbar from './components/Navbar';
import InstallBanner from './components/InstallBanner';
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp';
import {
  Auth,
  AuthCallback,
  Dashboard,
  DocumentEditor,
  Documents,
  Templates,
  Settings,
  NotFound,
  LoadDocument,
  Pricing,
  Success,
  PaymentSetup,
  APIDocumentation
} from './pages';
import Documentation from './pages/Documentation';

function App() {
  const { setUser, setSession, initialize } = useAuthStore();

  useEffect(() => {
    // Initialize auth state
    initialize();

    // Listen for changes on auth state (sign in, sign out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setSession, initialize]);

  return (
    <LanguageProvider>
    <Router>
      <div className="min-h-screen relative bg-gray-50 dark:bg-gray-900">
        {/* Decorative background patterns */}
        <div className="fixed inset-0 z-0">
          {/* Main background image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-50 dark:opacity-40"
            style={{
              backgroundImage: `url('https://github.com/slothcoin035/Dochelper-Agent-App/blob/main/MainImage4.png?raw=true')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />

          {/* Top-left pattern */}
          <div 
            className="absolute top-0 left-0 w-[800px] h-[600px] opacity-50 dark:opacity-40"
            style={{
              backgroundImage: `radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.1) 0%, rgba(124, 58, 237, 0) 50%)`,
              transform: 'translate(-20%, -20%)'
            }}
          />

          {/* Bottom-right pattern */}
          <div 
            className="absolute bottom-0 right-0 w-[800px] h-[600px] opacity-50 dark:opacity-40"
            style={{
              backgroundImage: `radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 50%)`,
              transform: 'translate(20%, 20%)'
            }}
          />

          {/* Subtle grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '30px 30px'
            }}
          />
        </div>

        {/* Content gradient overlay */}
        <div 
          className="fixed inset-0 z-0 bg-gradient-to-br from-white/50 via-white/60 to-transparent dark:from-gray-900/60 dark:via-gray-900/70 dark:to-transparent"
        />

        {/* Main content */}
        <div className="relative z-10">
          <InstallBanner />
          <Navbar />
          <main className="container mx-auto px-4 py-8">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/editor/:id?" element={<DocumentEditor />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/load" element={<LoadDocument />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/success" element={<Success />} />
              <Route path="/payment-setup" element={<PaymentSetup />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/api-docs" element={<APIDocumentation />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>

        {/* Decorative elements */}
        <div className="fixed bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 z-50" />
        
        {/* Decorative corner accents */}
        <div className="fixed top-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-transparent z-0" />
        <div className="fixed top-0 right-0 w-24 h-24 bg-gradient-to-bl from-purple-500/10 to-transparent z-0" />
        <div className="fixed bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-500/10 to-transparent z-0" />
        <div className="fixed bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-blue-500/10 to-transparent z-0" />

        {/* Keyboard Shortcuts Help */}
        <KeyboardShortcutsHelp />
      </div>
    </Router>
    </LanguageProvider>
  );
}

export default App;